import * as React from 'react';
import React__default, { memo, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import debounce from 'lodash/debounce';
import { Icon } from '@grandvisionhq/icons';

function Article(_a) {
    var children = _a.children;
    return React.createElement("article", { className: "article" }, children);
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};
function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}
function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || from);
}

var posedIcon = {
    buttonWithDropdownIdle: {
        rotate: 0,
    },
    buttonWithDropdownIsActive: {
        rotate: 180,
    },
};
var posedContent = {
    enter: {
        opacity: 1,
        y: 0,
        overflow: 'hidden',
        transitionEnd: { overflow: '' },
    },
    exit: {
        opacity: 0,
        y: -25,
        overflow: 'hidden',
        transition: { duration: 0 },
    },
};
var ButtonWithDropdown = /** @class */ (function (_super) {
    __extends(ButtonWithDropdown, _super);
    function ButtonWithDropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            offset: 0,
            isActive: typeof _this.props.isActive === 'boolean'
                ? _this.props.isActive
                : _this.props.initialActive || false,
        };
        _this.contentRef = React__default.createRef();
        _this.handleResize = debounce(function () { return _this.setOffset(); }, 60);
        _this.setOffset = function () {
            if (_this.contentRef.current && !_this.props.isFullWidth) {
                var innerWidth_1 = window.innerWidth;
                var right = _this.contentRef.current.getBoundingClientRect().right;
                var scrollBarWidth = 25; // IE needs this
                var offset = Math.min(innerWidth_1 - (right - _this.state.offset + scrollBarWidth), 0);
                if (offset !== _this.state.offset) {
                    _this.setState({ offset: offset });
                }
            }
        };
        _this.handleClick = function () {
            var isControlled = typeof _this.props.isActive === 'boolean';
            var isActive = !(isControlled ? _this.props.isActive : _this.state.isActive);
            !isControlled && _this.setState(function () { return ({ isActive: isActive }); });
            _this.props.onClick && _this.props.onClick(isActive);
        };
        return _this;
    }
    ButtonWithDropdown.prototype.componentDidMount = function () {
        window.addEventListener('resize', this.handleResize);
        this.setOffset();
    };
    ButtonWithDropdown.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.handleResize);
    };
    ButtonWithDropdown.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (prevProps.isActive !== this.props.isActive || prevState.isActive !== this.state.isActive) {
            this.setOffset();
        }
    };
    ButtonWithDropdown.prototype.render = function () {
        var _a = this.props, label = _a.label, children = _a.children, isFullWidth = _a.isFullWidth, title = _a.title, isDisabled = _a.isDisabled, className = _a.className, rest = __rest(_a, ["label", "children", "isFullWidth", "title", "isDisabled", "className"]);
        var offset = this.state.offset;
        var isControlled = typeof this.props.isActive === 'boolean';
        var isActive = isControlled ? this.props.isActive : this.state.isActive;
        var dropdownClassnames = classnames('button-with-dropdown', {
            _is_full_width: isFullWidth,
        });
        var buttonClassnames = classnames('button-with-dropdown__button', className, {
            _is_active: isActive,
            _is_full_width: !!isFullWidth,
        });
        var contentClassnames = classnames('button-with-dropdown__content', {
            _is_active: isActive,
            _is_full_width: !!isFullWidth,
            _is_disabled: isDisabled,
        });
        return (React__default.createElement(motion.div, { className: dropdownClassnames, animate: isActive ? 'buttonWithDropdownIsActive' : 'buttonWithDropdownIdle' },
            React__default.createElement("button", { className: buttonClassnames, onClick: this.handleClick, type: "button", "aria-haspopup": "true", "aria-expanded": isActive, "aria-label": rest['aria-label'] || title, "aria-labelledby": rest['aria-labelledby'], "aria-describedby": rest['aria-describedby'] },
                React__default.createElement("span", { className: "button-with-dropdown__button-inner" },
                    React__default.createElement("span", { className: "button-with-dropdown__label" }, label),
                    !isDisabled && (React__default.createElement(motion.span, { className: "button-with-dropdown__icon", variants: posedIcon },
                        React__default.createElement(Icon, { name: "chevron-down" }))))),
            React__default.createElement("div", { className: "button-with-dropdown__content-container", style: { left: offset } },
                React__default.createElement(AnimatePresence, null, isActive && (React__default.createElement(motion.span, { initial: "exit", animate: "enter", exit: "exit", variants: posedContent, key: "_", className: contentClassnames, ref: this.contentRef }, children))))));
    };
    return ButtonWithDropdown;
}(React__default.Component));

var Checkbox = /** @class */ (function (_super) {
    __extends(Checkbox, _super);
    function Checkbox() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.inputRef = React.createRef();
        _this.onClick = function (e) {
            _this.props.onChange({
                value: _this.props.value,
                checked: e.currentTarget.checked,
            });
        };
        return _this;
    }
    Checkbox.prototype.render = function () {
        var _a = this.props, id = _a.id, checked = _a.checked, disabled = _a.disabled, rest = __rest(_a, ["id", "checked", "disabled"]);
        return (React.createElement("label", { className: classnames('checkbox', this.props.className) },
            React.createElement("input", { className: "checkbox__input", type: "checkbox", id: id, "aria-checked": checked, "aria-label": rest['aria-label'], checked: typeof checked === 'boolean' ? checked : false, disabled: disabled, onChange: disabled ? undefined : this.onClick, "data-t": this.props['data-t'], tabIndex: 0, ref: this.inputRef, value: "0" // Not really used anywhere but it's a required attribute
             }),
            React.createElement("div", { className: "checkbox__mask" }),
            checked === 'mixed' ? (React.createElement("div", { className: "checkbox__indeterminate" })) : (React.createElement("svg", { className: "checkbox__checkmark", width: "14", height: "13", viewBox: "0 0 14 13", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                React.createElement("path", { d: "M1 7.5L4.5 12L13 1", fill: "none" })))));
    };
    Checkbox.prototype.componentDidUpdate = function (prevProps) {
        if (this.inputRef.current) {
            this.inputRef.current.indeterminate = this.props.checked === 'mixed';
        }
    };
    Checkbox.defaultProps = {
        checked: false,
        disabled: false,
    };
    return Checkbox;
}(React.PureComponent));

var Container = function (_a) {
    var children = _a.children, className = _a.className;
    return (React.createElement("div", { className: classnames('container', className) }, children));
};

var Divider = function (_a) {
    var className = _a.className, _b = _a.orientation, orientation = _b === void 0 ? 'horizontal' : _b, _c = _a.component, Component = _c === void 0 ? orientation === 'horizontal' ? 'hr' : 'div' : _c, _d = _a.theme, theme = _d === void 0 ? 'regular' : _d, _e = _a.isFlexItem, isFlexItem = _e === void 0 ? false : _e, rest = __rest(_a, ["className", "orientation", "component", "theme", "isFlexItem"]);
    return (React.createElement(Component, __assign({ className: classnames('divider', "divider--" + orientation, {
            'divider--flex-item': isFlexItem,
            'divider--light': theme === 'light',
        }, className), role: "separator", "aria-orientation": orientation }, rest)));
};

var Hr = function (_a) {
    var className = _a.className;
    return (React.createElement("hr", { className: classnames('horizontal-rule', className) }));
};

var Label = function (_a) {
    var type = _a.type, className = _a.className, children = _a.children;
    return (React.createElement("b", { className: classnames('label', "label--" + type, className) }, children));
};

var Tag = function (_a) {
    var children = _a.children, onClick = _a.onClick;
    return (React.createElement("button", { className: "tag", onClick: onClick, type: "button" },
        children,
        React.createElement(Icon, { className: "tag__clear", name: "clear" })));
};

var Display = function (_a) {
    var _b;
    var _c = _a.align, align = _c === void 0 ? 'inherit' : _c, _d = _a.color, color = _d === void 0 ? 'inherit' : _d, className = _a.className, _e = _a.component, component = _e === void 0 ? 'h1' : _e, _f = _a["data-t"], testId = _f === void 0 ? 'typography-display' : _f, rest = __rest(_a, ["align", "color", "className", "component", 'data-t']);
    var Component = component || 'h1';
    return (React.createElement(Component, __assign({ className: classnames('typography-display', className, (_b = {},
            _b["typography-display--align-" + align] = align !== 'inherit',
            _b["typography-display--color-" + color] = color !== 'inherit',
            _b)), "data-t": testId }, rest)));
};

var variantMap$1 = {
    large: 'h1',
    medium: 'h2',
    small: 'h3',
    smallest: 'h4',
    subheading1: 'h5',
    subheading2: 'h6',
};
var Heading = function (_a) {
    var _b;
    var _c = _a.align, align = _c === void 0 ? 'inherit' : _c, className = _a.className, _d = _a.color, color = _d === void 0 ? 'inherit' : _d, component = _a.component, _e = _a.variant, variant = _e === void 0 ? 'large' : _e, _f = _a["data-t"], testId = _f === void 0 ? 'typography-heading' : _f, rest = __rest(_a, ["align", "className", "color", "component", "variant", 'data-t']);
    var Component = component || variantMap$1[variant];
    return (React.createElement(Component, __assign({ className: classnames('typography-heading', "typography-heading--" + variant, className, (_b = {},
            _b["typography-heading--align-" + align] = align !== 'inherit',
            _b["typography-heading--color-" + color] = color !== 'inherit',
            _b)), "data-t": testId }, rest)));
};

var variantMap = {
    body: 'p',
    'body-head': 'strong',
    caption: 'small',
    'caption-head': 'strong',
    link: 'span',
    list: 'span',
};
var Text = function (_a) {
    var _b;
    var _c = _a.align, align = _c === void 0 ? 'inherit' : _c, className = _a.className, _d = _a.color, color = _d === void 0 ? 'inherit' : _d, component = _a.component, _e = _a.variant, variant = _e === void 0 ? 'body' : _e, _f = _a["data-t"], testId = _f === void 0 ? 'typography-text' : _f, rest = __rest(_a, ["align", "className", "color", "component", "variant", 'data-t']);
    var Component = component || variantMap[variant];
    return (React.createElement(Component, __assign({ className: classnames('typography-text', "typography-text--" + variant, className, (_b = {},
            _b["typography-text--align-" + align] = align !== 'inherit',
            _b["typography-text--color-" + color] = color !== 'inherit',
            _b)), "data-t": testId }, rest)));
};

var TextArea = function (_a) {
    var className = _a.className, disabled = _a.disabled, maxLength = _a.maxLength, onChange = _a.onChange, onBlur = _a.onBlur, placeholder = _a.placeholder, name = _a.name, id = _a.id, rest = __rest(_a, ["className", "disabled", "maxLength", "onChange", "onBlur", "placeholder", "name", "id"]);
    var resolvedId = id || name;
    var handleChange = React__default.useCallback(function (e) {
        !disabled && onChange && onChange(e.currentTarget.value);
    }, [disabled, onChange]);
    var handleBlur = React__default.useCallback(function (e) {
        !disabled && onBlur && onBlur(e.currentTarget.value);
    }, [disabled, onBlur]);
    return (React__default.createElement("textarea", __assign({}, rest, { name: name, id: resolvedId, className: classnames('textarea', className), disabled: disabled, maxLength: maxLength, onChange: handleChange, onBlur: handleBlur, placeholder: placeholder })));
};

var ResponsiveImage = function (_a) {
    var src = _a.src, srcSet = _a.srcSet, alt = _a.alt, className = _a.className, sizes = _a.sizes, dataT = _a["data-t"];
    return (React.createElement("img", { "data-t": dataT || 'responsive-image', width: "100%", className: classnames('responsive-image', className), src: src, srcSet: srcSet, sizes: sizes, alt: alt }));
};

var ButtonLoader = memo(function () { return React__default.createElement("div", { "aria-hidden": "true", className: "button--loader" }); });

var Button = function (_a) {
    var children = _a.children, className = _a.className, _b = _a.type, type = _b === void 0 ? 'button' : _b, variant = _a.variant, size = _a.size, loading = _a.loading, loadingLabel = _a.loadingLabel, props = __rest(_a, ["children", "className", "type", "variant", "size", "loading", "loadingLabel"]);
    var classes = useMemo(function () {
        var _a;
        return classnames('btn', "btn--" + variant, (_a = {}, _a["btn--" + size] = variant !== 'ghost' && size && size !== 'medium', _a), className);
    }, [className, variant, size]);
    var a11y = useMemo(function () {
        if (!loading) {
            return {};
        }
        return __assign({ role: 'status', 'aria-live': 'polite' }, (loadingLabel && { 'aria-label': loadingLabel }));
    }, [loading, loadingLabel]);
    return (React__default.createElement("button", __assign({ type: type, className: classes }, props, a11y),
        loading && React__default.createElement(ButtonLoader, null),
        React__default.createElement("span", null, children)));
};

var ToggleButton = function (_a) {
    var value = _a.value, selected = _a.selected, _b = _a.type, type = _b === void 0 ? 'checkbox' : _b, onChange = _a.onChange, props = __rest(_a, ["value", "selected", "type", "onChange"]);
    var _c = __read(useState(selected), 2), active = _c[0], setActive = _c[1];
    function clickHandler() {
        if (active && type === 'radio') {
            return;
        }
        setActive(!active);
        if (onChange) {
            onChange(!active ? value : '');
        }
    }
    useEffect(function () {
        setActive(selected);
    }, [selected]);
    return (React__default.createElement(Button, __assign({ value: active ? value : undefined, onClick: clickHandler, variant: active ? 'solid' : 'outline' }, props)));
};

function optionsToValues(options) {
    return options
        .reduce(function (acc, opt) { return __spreadArray(__spreadArray([], __read(acc)), [opt.selected ? opt.value : '']); }, [])
        .filter(function (value) { return Boolean(value); });
}
var ButtonGroup = function (_a) {
    var _b;
    var className = _a.className, _c = _a.type, type = _c === void 0 ? 'checkbox' : _c, size = _a.size, block = _a.block, options = _a.options, onChange = _a.onChange, props = __rest(_a, ["className", "type", "size", "block", "options", "onChange"]);
    var classes = classnames('btn-group', (_b = {}, _b["btn-group--" + size] = size && size !== 'medium', _b), { 'btn-group--block': block }, className);
    var _d = __read(useState(options), 2), values = _d[0], setValues = _d[1];
    useEffect(function () {
        setValues(options);
    }, [options]);
    var handleChange = function (index, value) {
        var newOptions;
        switch (type) {
            case 'radio':
                newOptions = values.map(function (option, mapIndex) {
                    return __assign(__assign({}, option), { selected: index === mapIndex });
                });
                setValues(newOptions);
                if (onChange) {
                    var result = optionsToValues(newOptions)[0];
                    onChange(result);
                }
                break;
            case 'checkbox':
                newOptions = values.map(function (option, mapIndex) {
                    if (index === mapIndex) {
                        return __assign(__assign({}, option), { selected: option.value === value });
                    }
                    return __assign({}, option);
                });
                setValues(newOptions);
                if (onChange) {
                    var result = optionsToValues(newOptions);
                    onChange(result);
                }
                break;
        }
    };
    var a11y = useMemo(function () {
        return type === 'radio'
            ? {
                role: 'radiogroup',
                buttonRole: 'radio',
            }
            : {
                role: 'group',
                buttonRole: 'checkbox',
            };
    }, [type]);
    return (React__default.createElement("div", __assign({ className: classes }, props, { role: a11y.role }), values.map(function (opt, index) { return (React__default.createElement(ToggleButton, { role: a11y.buttonRole, type: type, size: size, value: opt.value, selected: opt.selected, "aria-checked": opt.selected, key: opt.value.toString(), onChange: function (val) { return handleChange(index, val); } }, opt.label)); })));
};

var RoundedButton = function (_a) {
    var children = _a.children, className = _a.className, _b = _a.type, type = _b === void 0 ? 'button' : _b, variant = _a.variant, size = _a.size, loading = _a.loading, loadingLabel = _a.loadingLabel, props = __rest(_a, ["children", "className", "type", "variant", "size", "loading", "loadingLabel"]);
    var classes = useMemo(function () {
        var _a;
        return classnames('rounded-button', "rounded-button--" + variant, (_a = {}, _a["rounded-button--" + size] = size && size !== 'medium', _a), className);
    }, [className, variant, size]);
    var a11y = useMemo(function () {
        if (!loading) {
            return {};
        }
        return __assign({ role: 'status', 'aria-live': 'polite' }, (loadingLabel && { 'aria-label': loadingLabel }));
    }, [loading, loadingLabel]);
    return (React__default.createElement("button", __assign({ type: type, className: classes }, props, a11y), loading ? React__default.createElement(ButtonLoader, null) : children));
};

var getSelectionBoxStyles = function (_a) {
    var _b = _a === void 0 ? {} : _a, isSelected = _b.isSelected, isDisabled = _b.isDisabled, className = _b.className;
    return classnames('selection-box', className, {
        _is_selected: isSelected && !isDisabled,
        _is_disabled: isDisabled,
    });
};

export { Article, Button, ButtonGroup, ButtonWithDropdown, Checkbox, Container, Display, Divider, Heading, Hr, Label, ResponsiveImage, RoundedButton, Tag, Text, TextArea, ToggleButton, getSelectionBoxStyles, variantMap$1 as variantMap };
//# sourceMappingURL=index.es.js.map
